import { queryOptions } from '@tanstack/react-query'
import { apiClient } from '.'
import { DietaryPatternCountStat, FoodEntryLogStats, IngredientCount, RecipeLogStats, User } from './types'

const DEFAULT_PAGE_SIZE = 20

export const topLoggedIngredientsQuery = ({ page = 0 }: { page?: number }) => queryOptions({
    queryKey: ['topLoggedIngredients', { page }],
    queryFn: () => fetchTopLoggedIngredients({ page })
})

export const topLoggedRecipesQuery = ({ page = 0 }: { page?: number }) => queryOptions({
    queryKey: ['topLoggedRecipes', { page }],
    queryFn: () => fetchTopLoggedRecipes({ page })
})

export const topLoggedOpenFoodFactsProductsQuery = ({ page = 0 }: { page?: number }) => queryOptions({
    queryKey: ['topLoggedOpenFoodFactsProducts', { page }],
    queryFn: () => fetchTopLoggedOpenFoodFactsProducts({ page })
})

export const topLoggedFoodEntriesQuery = ({ page = 0 }: { page?: number }) => queryOptions({
    queryKey: ['topLoggedFoodEntries', { page }],
    queryFn: () => fetchTopLoggedFoodEntries({ page })
})

export const topFavoriteIngredientsQuery = ({ page = 0 }: { page?: number }) => queryOptions({
    queryKey: ['topFavoriteIngredients', { page }],
    queryFn: () => fetchTopFavoriteIngredients({ page })
})

export const topFavoriteRecipesQuery = ({ page = 0 }: { page?: number }) => queryOptions({
    queryKey: ['topFavoriteRecipes', { page }],
    queryFn: () => fetchTopFavoriteRecipes({ page })
})

export const dietaryRestrictionsUsersCountQuery = () => queryOptions({
    queryKey: ['dietaryRestrictionsUsersCount'],
    queryFn: fetchDietaryRestrictionsUsersCount
})

export const ingredientRestrictionsUsersCountQuery = ({ page = 0 }: { page?: number }) => queryOptions({
    queryKey: ['ingredientRestrictionsUsersCount', { page }],
    queryFn: () => fetchIngredientRestrictionsUsersCount({ page })
})

export const foodLogCountPerDayQuery = ({ startDate, endDate }: { startDate: Date; endDate: Date }) => queryOptions({
    queryKey: ['foodLogCountPerDay', { startDate, endDate }],
    queryFn: () => fetchFoodLogCountPerDay({ startDate, endDate })
})

export const foodLogCountPerWeekDayQuery = () => queryOptions({
    queryKey: ['foodLogCountPerWeekDay'],
    queryFn: () => fetchFoodLogCountPerWeekDay()
})

export const foodLogCountPerHourQuery = () => queryOptions({
    queryKey: ['foodLogCountPerHour'],
    queryFn: () => fetchFoodLogCountPerHour()
})

export const rollingActiveUsersDailyQuery = ({
    startDate,
    endDate,
    windowDays = 7,
}: {
    startDate: Date
    endDate: Date
    windowDays?: number
}) =>
    queryOptions({
        queryKey: [
            'rollingActiveUsersDaily',
            { start: startDate.toISOString(), end: endDate.toISOString(), windowDays },
        ],
        queryFn: () => fetchRollingActiveUsersDaily({ startDate, endDate, windowDays }),
    })

export const rollingActiveUsersWeeklyQuery = ({
    startDate,
    endDate,
    windowDays = 7,
}: {
    startDate: Date
    endDate: Date
    windowDays?: number
}) =>
    queryOptions({
        queryKey: [
            'rollingActiveUsersWeekly',
            { start: startDate.toISOString(), end: endDate.toISOString(), windowDays },
        ],
        queryFn: () => fetchRollingActiveUsersWeekly({ startDate, endDate, windowDays }),
    })

export const rollingActiveUsersMonthlyQuery = ({
    startDate,
    endDate,
    windowDays = 7,
}: {
    startDate: Date
    endDate: Date
    windowDays?: number
}) =>
    queryOptions({
        queryKey: [
            'rollingActiveUsersMonthly',
            { start: startDate.toISOString(), end: endDate.toISOString(), windowDays },
        ],
        queryFn: () => fetchRollingActiveUsersMonthly({ startDate, endDate, windowDays }),
    })

export const mealPlanAdherenceQuery = ({
    startDate,
    endDate,
}: {
    startDate?: Date
    endDate?: Date
}) =>
    queryOptions({
        queryKey: ['mealPlanAdherence', { start: startDate?.toISOString(), end: endDate?.toISOString() }],
        queryFn: () => fetchMealPlanAdherence({ startDate, endDate }),
    })

export const dayOverMonthActiveUsersQuery = () =>
    queryOptions({
        queryKey: ['dayOverMonthActiveUsers'],
        queryFn: fetchDayOverMonthActiveUsers,
    })

export const avgUserWeightChangeQuery = () =>
    queryOptions({
        queryKey: ['avgUserWeightChange'],
        queryFn: fetchAvgUserWeightChange,
    })

export const adherenceTrendQuery = ({
    startDate,
    endDate,
}: {
    startDate: Date
    endDate: Date
}) =>
    queryOptions({
        queryKey: ['adherenceTrend', { start: startDate.toISOString(), end: endDate.toISOString() }],
        queryFn: () => fetchAdherenceTrend({ startDate, endDate }),
    })

export const adherenceWeightChangeQuery = ({
    startDate,
    endDate,
}: {
    startDate: Date
    endDate: Date
}) =>
    queryOptions({
        queryKey: ['adherenceWeightChange', { start: startDate.toISOString(), end: endDate.toISOString() }],
        queryFn: () => fetchAdherenceWeightChange({ startDate, endDate }),
    })

export const retentionCurveQuery = ({
    startDate,
    endDate,
    windowDays = 9,
}: {
    startDate: Date
    endDate: Date
    windowDays?: number
}) =>
    queryOptions({
        queryKey: ['retentionCurve', { start: startDate.toISOString(), end: endDate.toISOString(), windowDays }],
        queryFn: () => fetchRetentionCurve({ startDate, endDate, windowDays }),
    })

// Users KPIs
export const usersEnrolledCountQuery = () =>
    queryOptions({
        queryKey: ['usersEnrolledCount'],
        queryFn: fetchUsersEnrolledCount,
    })

export const usersActiveCountQuery = () =>
    queryOptions({
        queryKey: ['usersActiveCount'],
        queryFn: fetchUsersActiveCount,
    })


async function fetchTopLoggedIngredients({ page = 0 }: { page: number }): Promise<IngredientCount[]> {
    return apiClient.get(`/usage-statistics/top-logged-ingredients`, {
        params: {
            offset: DEFAULT_PAGE_SIZE * page,
            count: DEFAULT_PAGE_SIZE,
        }
    }).then((res) => res.data)
}


async function fetchTopLoggedRecipes({ page = 0 }: { page: number }): Promise<RecipeLogStats[]> {
    return apiClient.get(`/usage-statistics/top-logged-recipes`, {
        params: {
            offset: DEFAULT_PAGE_SIZE * page,
            count: DEFAULT_PAGE_SIZE,
        }
    }).then((res) => res.data)
}


async function fetchTopLoggedOpenFoodFactsProducts({ page = 0 }: { page: number }): Promise<RecipeLogStats[]> {
    return apiClient.get(`/usage-statistics/top-logged-off-products`, {
        params: {
            offset: DEFAULT_PAGE_SIZE * page,
            count: DEFAULT_PAGE_SIZE,
        }
    }).then((res) => res.data)
}

async function fetchTopLoggedFoodEntries({ page = 0 }: { page: number }): Promise<FoodEntryLogStats[]> {
    return apiClient.get(`/usage-statistics/top-logged-food-entries`, {
        params: {
            offset: DEFAULT_PAGE_SIZE * page,
            count: DEFAULT_PAGE_SIZE,
        }
    }).then((res) => res.data)
}

async function fetchTopFavoriteIngredients({ page = 0 }: { page: number }): Promise<IngredientCount[]> {
    return apiClient.get(`/usage-statistics/top-favorited-ingredients`, {
        params: {
            offset: DEFAULT_PAGE_SIZE * page,
            count: DEFAULT_PAGE_SIZE,
        }
    }).then((res) => res.data)
}

async function fetchTopFavoriteRecipes({ page = 0 }: { page: number }): Promise<RecipeLogStats[]> {
    return apiClient.get(`/usage-statistics/top-favorited-recipes`, {
        params: {
            offset: DEFAULT_PAGE_SIZE * page,
            count: DEFAULT_PAGE_SIZE,
        }
    }).then((res) => res.data)
}

export async function fetchDietaryRestrictionsUsersCount(): Promise<Array<DietaryPatternCountStat>> {
    const res = await apiClient.get(`/usage-statistics/dietary-restrictions-users-count`)
    return res.data
}

export async function fetchIngredientRestrictionsUsersCount({ page }: { page: number }): Promise<Array<IngredientCount>> {
    const res = await apiClient.get(`/usage-statistics/restricted-ingredients-users-count`, {
        params: {
            offset: DEFAULT_PAGE_SIZE * page,
            count: DEFAULT_PAGE_SIZE,
        }
    })
    return res.data
}

export async function fetchFoodLogCountPerDay({ startDate, endDate }: { startDate: Date; endDate: Date }): Promise<Array<{ date: Date; count: number }>> {
    const res = await apiClient.get(`/usage-statistics/food-logs-per-day`, {
        params: {
            startDate,
            endDate
        }
    })
    return res.data.map((item: any) => ({ ...item, date: new Date(item.date) }))
}

export async function fetchFoodLogCountPerWeekDay(): Promise<Array<{ weekDay: string; count: number }>> {
    const res = await apiClient.get(`/usage-statistics/food-logs-per-week-day`)
    return res.data
}

export async function fetchFoodLogCountPerHour(): Promise<Array<{ hour: number; count: number }>> {
    const res = await apiClient.get(`/usage-statistics/food-logs-per-hour`)
    return res.data
}

type MealPlanAdherenceResponse = {
    recommendedMeals: number
    loggedMeals: number
    adherencePercentage: number
}

type DayOverMonthActiveUsersResponse = {
    result: number
}

type AvgUserWeightChangeResponse = {
    kgChange: number
}

type AdherenceTrendResponse = {
    weekStart: string
    weekEndExclusive: string
    recommendedMeals: number
    loggedMeals: number
    adherencePercentage: number
}

type AdherenceWeightChangeResponse = {
    userId: number
    adherencePercentage: number
    weightChangeKg: number
}

type RetentionCurveResponse = {
    dayOffset: number
    survivalPercentage: number
}

type RollingActiveUsersDailyResponse = {
    bucketDate: string
    rollingActiveUsers: number
}

type RollingActiveUsersWeeklyResponse = {
    weekStart: string
    weekEndExclusive: string
    rollingActiveUsers: number
}

type RollingActiveUsersMonthlyResponse = {
    monthStart: string
    monthEndExclusive: string
    rollingActiveUsers: number
}

async function fetchRollingActiveUsersDaily({
    startDate,
    endDate,
    windowDays = 7,
}: {
    startDate: Date
    endDate: Date
    windowDays?: number
}): Promise<Array<{ date: Date; count: number }>> {
    const res = await apiClient.get(`/usage-statistics/rolling-active-users/daily`, {
        params: {
            fromUtc: startDate.toISOString(),
            toUtc: endDate.toISOString(),
            windowDays,
        },
    })
    return res.data.map((item: RollingActiveUsersDailyResponse) => ({
        date: new Date(item.bucketDate),
        count: item.rollingActiveUsers,
    }))
}

async function fetchRollingActiveUsersWeekly({
    startDate,
    endDate,
    windowDays = 7,
}: {
    startDate: Date
    endDate: Date
    windowDays?: number
}): Promise<Array<{ date: Date; endDate: Date; count: number }>> {
    const res = await apiClient.get(`/usage-statistics/rolling-active-users/weekly`, {
        params: {
            fromUtc: startDate.toISOString(),
            toUtc: endDate.toISOString(),
            windowDays,
        },
    })
    return res.data.map((item: RollingActiveUsersWeeklyResponse) => ({
        date: new Date(item.weekStart),
        endDate: new Date(item.weekEndExclusive),
        count: item.rollingActiveUsers,
    }))
}

async function fetchRollingActiveUsersMonthly({
    startDate,
    endDate,
    windowDays = 7,
}: {
    startDate: Date
    endDate: Date
    windowDays?: number
}): Promise<Array<{ date: Date; endDate: Date; count: number }>> {
    const res = await apiClient.get(`/usage-statistics/rolling-active-users/monthly`, {
        params: {
            fromUtc: startDate.toISOString(),
            toUtc: endDate.toISOString(),
            windowDays,
        },
    })
    return res.data.map((item: RollingActiveUsersMonthlyResponse) => ({
        date: new Date(item.monthStart),
        endDate: new Date(item.monthEndExclusive),
        count: item.rollingActiveUsers,
    }))
}

async function fetchMealPlanAdherence({
    startDate,
    endDate,
}: {
    startDate?: Date
    endDate?: Date
}): Promise<MealPlanAdherenceResponse> {
    const res = await apiClient.get(`/usage-statistics/meal-plan-adherence`, {
        params: {
            fromUtc: startDate?.toISOString(),
            toUtc: endDate?.toISOString(),
        },
    })
    return res.data
}

async function fetchDayOverMonthActiveUsers(): Promise<number> {
    const res = await apiClient.get(`/usage-statistics/day-over-month-active-users`)
    const data: DayOverMonthActiveUsersResponse = res.data
    return data.result
}

async function fetchAvgUserWeightChange(): Promise<number> {
    const res = await apiClient.get(`/usage-statistics/avg-user-weight-change`)
    const data: AvgUserWeightChangeResponse = res.data
    return data.kgChange
}

async function fetchAdherenceTrend({
    startDate,
    endDate,
}: {
    startDate: Date
    endDate: Date
}): Promise<Array<{ date: Date; value: number }>> {
    const res = await apiClient.get(`/usage-statistics/adherence-trend`, {
        params: {
            fromUtc: startDate.toISOString(),
            toUtc: endDate.toISOString(),
        },
    })
    const data: AdherenceTrendResponse[] = res.data
    return data.map((item) => ({
        date: new Date(item.weekStart),
        value: item.adherencePercentage,
    }))
}

async function fetchAdherenceWeightChange({
    startDate,
    endDate,
}: {
    startDate: Date
    endDate: Date
}): Promise<Array<{ adherence: number; weightChange: number }>> {
    const res = await apiClient.get(`/usage-statistics/adherence-weight-change`, {
        params: {
            fromUtc: startDate.toISOString(),
            toUtc: endDate.toISOString(),
        },
    })
    const data: AdherenceWeightChangeResponse[] = res.data
    return data.map((item) => ({
        adherence: item.adherencePercentage,
        weightChange: item.weightChangeKg,
    }))
}

async function fetchRetentionCurve({
    startDate,
    endDate,
    windowDays = 9,
}: {
    startDate: Date
    endDate: Date
    windowDays?: number
}): Promise<Array<{ x: number; y: number }>> {
    const res = await apiClient.get(`/usage-statistics/retention-curve`, {
        params: {
            fromUtc: startDate.toISOString(),
            toUtc: endDate.toISOString(),
            windowDays,
        },
    })
    const data: RetentionCurveResponse[] = res.data
    return data.map((item) => ({ x: item.dayOffset, y: item.survivalPercentage }))
}


export async function fetchUsersEnrolledCount(): Promise<Array<User>> {
    const res = await apiClient.get(`/usage-statistics/users-enrolled`)
    return res.data
}

export async function fetchUsersActiveCount(): Promise<Array<User>> {
    const res = await apiClient.get(`/usage-statistics/users-active`)
    return res.data
}

export const ACTIVITY_EVENT_TYPES = [
	'FOOD_LOG',
	'RECIPE_FAVORITE',
	'RECIPE_LESS_OFTEN',
	'RECIPE_NOT_RECOMMEND',
	'INGREDIENT_FAVORITE',
	'MEAL_PLAN',
] as const

export async function exportActivityEvents({
    startDate,
    endDate,
    includeNutrition,
    eventTypes,
}: {
    startDate: Date
    endDate: Date
    includeNutrition?: boolean
    eventTypes?: string[]
}): Promise<Blob> {
    const allTypesSelected = !eventTypes || eventTypes.length === ACTIVITY_EVENT_TYPES.length
    const res = await apiClient.get('/usage-statistics/activity-events/export', {
        params: {
            fromUtc: startDate.toISOString(),
            toUtc: endDate.toISOString(),
            ...(includeNutrition ? { includeNutrition: true } : {}),
            ...(allTypesSelected ? {} : { eventTypes: (eventTypes ?? []).join(',') }),
        },
        responseType: 'blob',
    })
    return res.data
}
